import React from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import PageMeta from '../components/PageMeta';

const reportCatalog = {
  'infosys-q2-fy27': {
    slug: 'infosys-q2-fy27',
    route: 'equity',
    title: 'Infosys Q2 FY27 Research Report',
    sector: 'Information Technology',
    summary:
      'Infosys posted resilient operating performance in the latest quarter, with digital services demand holding firm and margin discipline helping offset a more cautious discretionary spend environment. The report outlines the key business drivers, where execution remains strongest, and the strategic questions investors need to answer before the next earnings cycle.',
    keyFindings: [
      'Digital services pipeline remained stable, with large deal conversions continuing to support revenue quality.',
      'Operating margin held up despite wage inflation, suggesting cost discipline and productivity gains are offsetting near-term pressure.',
      'The company remains well positioned in AI-assisted service delivery, though investor focus remains on monetisation timing and execution quality.',
    ],
    methodology: 'This public summary draws on reported quarterly commentary, management disclosures, sector trends, and comparable operating metrics from recent earnings cycles.',
    publishedAt: '2026-10-12',
    updatedAt: '2026-10-12',
    author: 'The B Tree Research Team',
    image: 'https://www.thebtree.com/logo.png',
  },
  'india-power-q4-fy27': {
    slug: 'india-power-q4-fy27',
    route: 'equity',
    title: 'India Power Q4 FY27 Research Brief',
    sector: 'Power & Utilities',
    summary:
      'India power demand remained constructive as industrial and commercial consumption stayed firm, while transmission and grid upgrades continued to matter for operating leverage. The brief looks at balance-sheet discipline, utilization trends, and the policy backdrop shaping the next wave of capacity additions.',
    keyFindings: [
      'Capacity additions continued to be front-loaded in transmission and grid-oriented infrastructure.',
      'Utilization trends remained supportive for efficient operators while weaker peers faced margin compression.',
      'Policy tailwinds remained positive, though financing discipline and execution quality will determine which players compound faster.',
    ],
    methodology: 'This brief combines sector capacity data, power demand trends, regulatory updates, and market commentary to highlight the operating and financial factors that matter most.',
    publishedAt: '2026-10-11',
    updatedAt: '2026-10-12',
    author: 'The B Tree Research Team',
    image: 'https://www.thebtree.com/logo.png',
  },
  'banking-credit-cycle-2026': {
    slug: 'banking-credit-cycle-2026',
    route: 'sector',
    title: 'Banking Credit Cycle 2026',
    sector: 'Banking & Financial Services',
    summary:
      'Credit growth remained resilient across core banking segments, while deposit mix, credit costs, and funding discipline continued to define relative strength. This sector brief isolates the operating levers that matter most for earnings quality and capital efficiency in the next phase of the credit cycle.',
    keyFindings: [
      'Funding stability remained central to sector earnings quality, with deposit competition still shaping margin outcomes.',
      'Asset quality remained manageable, though underwriting discipline and provisioning expectations stayed key watchpoints.',
      'The most durable lenders continued to combine balance-sheet discipline with selective growth, improving earnings resilience.',
    ],
    methodology: 'This public summary combines sector lending data, funding trends, regulatory commentary and balance-sheet analysis to explain the real drivers behind bank earnings quality.',
    publishedAt: '2026-10-08',
    updatedAt: '2026-10-12',
    author: 'The B Tree Research Team',
    image: 'https://www.thebtree.com/logo.png',
  },
  'judicial-reform-brief-2026': {
    slug: 'judicial-reform-brief-2026',
    route: 'legal',
    title: 'Judicial Reform & Institutional Capacity Brief',
    sector: 'Legal & Governance',
    summary:
      'Judicial reform and institutional capacity remain central to the speed and quality of governance outcomes. This brief explains how procedural efficiency, case management reform, and institutional design interact with the broader legal and economic environment.',
    keyFindings: [
      'Institutional reform mattered as much as substantive legal changes in determining execution outcomes.',
      'Delayed adjudication continued to affect investment confidence, especially in sectors tied to regulatory clarity.',
      'The legal system’s capacity to absorb large policy shifts was increasingly linked to process modernization and digital enforcement tools.',
    ],
    methodology: 'The summary synthesizes legal developments, governance analysis, institutional data and public policy commentary to frame the practical implications of reform.',
    publishedAt: '2026-10-05',
    updatedAt: '2026-10-12',
    author: 'The B Tree Research Team',
    image: 'https://www.thebtree.com/logo.png',
  },
  'governance-priorities-2026': {
    slug: 'governance-priorities-2026',
    route: 'upsc',
    title: 'Governance Priorities 2026',
    sector: 'Public Policy & Governance',
    summary:
      'India’s governance agenda remains shaped by the intersection of infrastructure, inclusion, digital administration, and state capacity. This brief highlights the policy signals most relevant for administrators, students, and public affairs watchers in the current cycle.',
    keyFindings: [
      'Governance outcomes were increasingly tied to implementation quality rather than headline policy announcements alone.',
      'Digital public infrastructure continued to shape service delivery, transparency, and administrative accountability.',
      'The most important reforms combined policy intent with local delivery capability and measurable execution indicators.',
    ],
    methodology: 'This brief synthesizes policy announcements, execution trends, governance indicators and wider public affairs commentary into a practical decision-making framework.',
    publishedAt: '2026-10-02',
    updatedAt: '2026-10-12',
    author: 'The B Tree Research Team',
    image: 'https://www.thebtree.com/logo.png',
  },
};

const PublicReportPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const report = reportCatalog[slug] || reportCatalog['infosys-q2-fy27'];
  const routePrefix = location.pathname.split('/')[1] || report.route;
  const canonical = `https://www.thebtree.com/${routePrefix}/${slug || report.slug}`;

  return (
    <>
      <PageMeta
        title={`${report.title} | The B Tree`}
        description={report.summary}
        canonical={canonical}
        image={report.image}
        type="article"
      />

      <main className="min-h-screen bg-[#F7F5F2] text-slate-800">
        <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
          <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-16">
            <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-4">
              {report.sector}
            </p>
            <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-5">
              {report.title}
            </h1>
            <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-6">
              <span>Published: {report.publishedAt}</span>
              <span>Updated: {report.updatedAt}</span>
              <span>Author: {report.author}</span>
            </div>
            <p className="text-lg leading-8 max-w-3xl text-slate-700">{report.summary}</p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12">
          <div className="grid gap-8 lg:grid-cols-[1.3fr_0.7fr] items-start">
            <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-4">
                Free Summary
              </h2>
              <p className="text-slate-700 leading-8 mb-4">{report.summary}</p>

              <h3 className="text-xl font-bold text-[#0D1B2A] mt-8 mb-3">Key findings</h3>
              <ul className="space-y-3">
                {report.keyFindings.map((finding) => (
                  <li key={finding} className="flex gap-3 text-slate-700 leading-7">
                    <span className="mt-2 h-2.5 w-2.5 rounded-full bg-[#C8A84B] shrink-0" />
                    <span>{finding}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-[#0D1B2A] mt-8 mb-3">Methodology</h3>
              <p className="text-slate-700 leading-8">{report.methodology}</p>
            </article>

            <aside className="bg-[#0D1B2A] text-white rounded-2xl p-6 shadow-sm">
              <p className="text-[11px] font-mono tracking-[0.25em] uppercase text-[#C8A84B] font-semibold mb-4">
                Premium access
              </p>
              <h3 className="text-2xl font-[Cormorant_Garamond] font-medium mb-4">Free summary • full report behind subscription</h3>
              <p className="text-slate-200 leading-7 mb-6">
                The detailed report, charts, and analyst commentary are available to subscribers. The free summary is intended to provide useful context without exposing protected content.
              </p>
              <Link
                to="/subscription"
                className="inline-flex items-center justify-center rounded-full bg-[#C8A84B] px-5 py-3 text-sm font-semibold text-[#0D1B2A] hover:bg-[#D9BC5B] transition-colors"
              >
                Unlock full report
              </Link>
            </aside>
          </div>
        </section>
      </main>
    </>
  );
};

export default PublicReportPage;
